import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { searchLocations, type Location } from "@/api/endpoints/locations";
import { useBackGuard } from "@/app/back";
import { useNativeTitle } from "@/app/nativeTitle";
import { useBootstrap } from "@/app/providers";

const SEARCH_TITLE = "ค้นหาที่อยู่";

export function LocationSearchField({
  label,
  value,
  onSelect,
  required = false,
}: {
  label: string;
  value: string;
  onSelect: (location: Location) => void;
  required?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const empty = value.length === 0;

  return (
    <>
      <div>
        <span className="text-14 text-field-label mb-1 block">
          {label}
          {required ? (
            <span className="text-primary" aria-hidden="true">
              {" "}
              *
            </span>
          ) : null}
          {required ? <span className="sr-only"> (จำเป็น)</span> : null}
        </span>
        <button
          type="button"
          aria-label={label}
          onClick={() => {
            setOpen(true);
          }}
          className="text-16 border-input-field bg-secondary relative flex h-11 w-full items-center justify-between gap-2 rounded-[10px] border px-3 text-left text-field-value"
        >
          <span>{empty ? "" : value}</span>
          <SearchIcon />
        </button>
      </div>

      {open ? (
        <LocationSearchScreen
          onSelect={(location) => {
            onSelect(location);
            setOpen(false);
          }}
          onClose={() => {
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

function LocationSearchScreen({
  onSelect,
  onClose,
}: {
  onSelect: (location: Location) => void;
  onClose: () => void;
}) {
  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  const { data } = useBootstrap();
  const native = data.headerMode === "native";

  // Back closes this overlay rather than leaving the screen underneath, which
  // is the only thing the customer can mean while the search is covering it.
  // The native header's own back button lands here too, through the same
  // nav.backPressed event, so borrowing that header costs nothing.
  useBackGuard(() => {
    onClose();
    return true;
  });

  useNativeTitle(SEARCH_TITLE, native);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(term.trim());
    }, 1000);
    return () => {
      clearTimeout(timer);
    };
  }, [term]);

  const results = useQuery({
    queryKey: ["locations", debounced],
    queryFn: () => searchLocations(debounced),
    enabled: debounced.length > 0,
  });

  const list = debounced.length === 0 ? [] : (results.data ?? []);
  const trimmedTerm = term.trim();
  const loading =
    trimmedTerm.length > 0 && (trimmedTerm !== debounced || results.isFetching);
  const failed = !loading && results.isError;
  const searched = !loading && !failed;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white tracking-[-0.011em]">
      {native ? null : (
        <header className="safe-top flex h-14 shrink-0 items-center px-2">
          <button
            type="button"
            aria-label="ย้อนกลับ"
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center"
          >
            <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
              <path
                d="M15 5 8 12l7 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <h2 className="text-18 flex-1 pr-11 text-center font-bold">
            {SEARCH_TITLE}
          </h2>
        </header>
      )}

      {/* The native header already sits above the WebView, so nothing is left
          of the safe-area inset to pad for here. */}
      <div className={`shrink-0 px-4 ${native ? "pt-4" : ""}`}>
        <div className="relative">
          <input
            type="text"
            autoFocus
            value={term}
            placeholder="ค้นหาด้วย..."
            onChange={(event) => {
              setTerm(event.target.value);
            }}
            className="text-16 border-input-field bg-secondary h-11 w-full rounded-[10px] border pr-11 pl-3 text-field-value outline-none"
          />
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            <SearchIcon />
          </span>
        </div>
        <p className="text-14 text-primary mt-1 leading-5">ระบุแขวง/ตำบล, เขต/อำเภอ, จังหวัด, รหัสไปรษณีย์</p>
      </div>

      <div className="safe-bottom mt-4 flex-1 overflow-y-auto px-4">
        {loading ? (
          <p className="text-16 text-field-label mt-12 text-center">กำลังค้นหา...</p>
        ) : failed ? (
          <div className="mt-12 text-center">
            <p className="text-16 text-field-label">ค้นหาไม่สำเร็จ กรุณาลองใหม่อีกครั้ง</p>
            <button
              type="button"
              onClick={() => {
                void results.refetch();
              }}
              className="text-16 rounded-pill border-primary mt-4 h-btn-sm border px-6 font-bold text-primary"
            >
              ลองใหม่
            </button>
          </div>
        ) : list.length > 0 ? (
          <>
            <p className="text-16 text-field-label mb-2 leading-6 font-medium">
              {`รายการที่พบ ${list.length}`}
            </p>
            {list.map((location) => (
              <button
                key={`${location.subdistrict}-${location.district}-${location.postCode}`}
                type="button"
                onClick={() => {
                  onSelect(location);
                }}
                className="flex w-full flex-col items-start gap-1 border-b border-divider-soft py-3 text-left"
              >
                <span className="text-16 text-field-value leading-6">
                  {`${location.subdistrict}  >>  ${location.district}  >>  ${location.province}`}
                </span>
                <span className="text-14 text-field-label leading-5">
                  {`รหัสไปรษณีย์: ${location.postCode}`}
                </span>
              </button>
            ))}
          </>
        ) : searched ? (
          <p className="text-16 text-field-label mt-12 text-center">ไม่พบที่อยู่ที่ต้องการ</p>
        ) : null}
      </div>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5 shrink-0 text-field-label"
      aria-hidden="true"
    >
      <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <circle cx="11" cy="11" r="6" />
        <path d="m16 16 4 4" />
      </g>
    </svg>
  );
}
